import { type CSSProperties, useMemo, useState } from 'react';
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
} from 'react-bootstrap';
import {
  ArrowLeft,
  CheckCircleFill,
  ExclamationCircle,
  Globe,
  Instagram,
  Trash3Fill,
  Wifi,
} from 'react-bootstrap-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MultiSelect } from 'react-multi-select-component';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReactStars from 'react-rating-star-with-type';
import SignatureCanvas from 'react-signature-canvas';
import { GuestPortalContext } from '../../../modules/context/guestPortalContextV2';
import type { GuestTheme } from '../../../modules/interfaces';
import { resolveThemeVars } from '../../../modules/utils/guestTheme';
import { CheckInCard } from '../../Cards/CheckInCardV2';
import { ReservationCard } from '../../Cards/ReservationVerifyCard';
import { SocialNetworkCard } from '../../Cards/SocialNetworkCard';
import GuestPageHeader from '../../GuestPagesV2/Header';
import { buildPreviewContext } from './previewFixtures';

const Caption = ({ children }: { children: string }) => (
  <span className="theme-preview-caption">{children}</span>
);

/**
 * Live, themed preview that mounts the REAL guest-portal building blocks against a mocked
 * GuestPortalContext (no network calls). The draft theme is applied as inline CSS custom
 * properties on the `#guest-portal` wrapper so every nested portal class resolves to it.
 */
export const ThemePreview = ({ theme }: { theme: GuestTheme }) => {
  const vars = useMemo(() => resolveThemeVars(theme), [theme]);
  const previewStyle = vars as unknown as CSSProperties;
  // The signature canvas can't read CSS vars, so pass the resolved ink color directly.
  const inkColor = vars['--signature-ink-color'];
  const context = useMemo(() => buildPreviewContext(), []);
  const [phone, setPhone] = useState('15551234567');
  const [date, setDate] = useState<Date | null>(new Date());
  const [time, setTime] = useState<Date | null>(new Date());
  const [multi, setMulti] = useState<{ label: string; value: string }[]>([
    { label: 'Opt One', value: '1' },
  ]);
  const [hasSigned, setHasSigned] = useState(false);

  return (
    <div id="guest-portal" className="guest-theme-preview" style={previewStyle}>
      <GuestPortalContext.Provider value={context}>
        <Container fluid className="page-container guest-portal" style={previewStyle}>
          <Card className="card-item">
            <GuestPageHeader type="firstRegistration" />
            <div className="container-paddings pt-16px pb-16">
              {/* Back button — only renders on the verify-screen header in the real portal,
                  shown here standalone so its theming is visible. */}
              <Caption>Back button</Caption>
              <div className="back-button-circle">
                <ArrowLeft size={18} />
              </div>

              {/* Header separator */}
              <hr />

              {/* Registration step — inputs use the real editor-control/guest-inputs classes */}
              <Caption>Registration</Caption>
              <ReservationCard
                icon={<CheckCircleFill size={18} />}
                title="Verify your booking"
                checked
                showModal={() => {}}
              />
              <Form.Group className="mb-8px">
                <Form.Label className="form-label">Full name</Form.Label>
                <Form.Control
                  className="editor-control guest-inputs"
                  defaultValue="Alex Morgan"
                />
              </Form.Group>
              <Form.Group className="mb-8px">
                <Form.Label className="form-label">Email Address</Form.Label>
                <Form.Control
                  className="editor-control guest-inputs"
                  placeholder="Enter your Email..."
                />
              </Form.Group>
              <Form.Group className="mb-8px">
                <Form.Label className="form-label">Phone number</Form.Label>
                <PhoneInput
                  country="us"
                  value={phone}
                  onChange={setPhone}
                  inputStyle={{ width: '100%', height: '44px' }}
                  countryCodeEditable={false}
                />
              </Form.Group>
              <Form.Group className="mb-8px">
                <Form.Label className="form-label">Date of Birth</Form.Label>
                <div>
                  <DatePicker inline selected={date} onChange={setDate} />
                </div>
              </Form.Group>
              <Form.Group className="mb-8px">
                <Form.Label className="form-label">Time picker</Form.Label>
                <div>
                  <DatePicker
                    inline
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={60}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    selected={time}
                    onChange={setTime}
                  />
                </div>
              </Form.Group>
              <Form.Group className="mb-8px">
                <Form.Label className="form-label">How did you hear about us?</Form.Label>
                <Form.Select className="editor-control guest-inputs">
                  <option>Internet Search</option>
                  <option>Friend</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-8px">
                <Form.Label className="form-label">Preferences (multi-select)</Form.Label>
                <MultiSelect
                  options={[
                    { label: 'Opt One', value: '1' },
                    { label: 'Opt Two', value: '2' },
                    { label: 'Opt Three', value: '3' },
                  ]}
                  value={multi}
                  onChange={setMulti}
                  labelledBy="Select"
                />
              </Form.Group>
              <span className="terms-privacy-text">Terms &amp; Conditions</span>

              {/* Access code + check-in/out dates + warning message */}
              <Caption>Access code &amp; check-in</Caption>
              {/* Wrapped in .table-title so it picks up `#guest-portal .table-title h6`
                  (14px), matching the live portal size. */}
              <div className="table-title">
                <h6
                  className="mb-0 warning-title"
                  style={{ color: 'var(--secondary-font-color)' }}
                >
                  Your door code will not become active until your scheduled check-in time
                </h6>
              </div>
              <Container
                style={{
                  width: '100%',
                  paddingLeft: 0,
                  paddingRight: 0,
                  backgroundColor: 'var(--access-code-bg-color, #fff)',
                  marginTop: '12px',
                }}
              >
                {/* Door-code row is the verified/inactive state -> shows the lock in the
                    white card area (not the green "go register" column). */}
                <CheckInCard
                  title="Your Door Code"
                  content={<h4 className="room-code">482851#</h4>}
                />
                <hr
                  style={{
                    margin: 0,
                    height: '1px',
                    border: 'none',
                    backgroundColor: 'var(--access-code-border-color, #979797)',
                    marginLeft: '15px',
                  }}
                />
                <CheckInCard
                  type="checkOut"
                  titleCheckIn="Check-In"
                  contentCheckIn={
                    <span className="reservation-date">Jun 20, 2026 @ 03:00 PM</span>
                  }
                  title="Check-out"
                  content={
                    <span className="reservation-date">Jun 21, 2026 @ 11:00 AM</span>
                  }
                  active
                />
              </Container>

              {/* Customize your stay / add-ons */}
              <Caption>Customize your stay</Caption>
              <Row>
                <Col xs={6}>
                  <div className="customize-stay-container" />
                  <div className="customize-stay-subtitle">Early check-in</div>
                </Col>
                {/* Selected add-on: the count + remove badges overlay the top corners of
                    the image (absolutely positioned at their static spot, as in the portal). */}
                <Col xs={6}>
                  <Row>
                    <Col>
                      <Badge className="quantity-badge" bg="danger">
                        1
                      </Badge>
                    </Col>
                    <Col style={{ display: 'flex', justifyContent: 'end' }}>
                      <Badge className="notify-badge" bg="danger">
                        <Trash3Fill size={18} />
                      </Badge>
                    </Col>
                  </Row>
                  <div className="customize-stay-selected-container" />
                  <div className="customize-stay-subtitle">Champagne</div>
                </Col>
              </Row>

              {/* Accordions -> expanded header uses the container background, collapsed
                  header uses the page background. Show one of each so both states are
                  visible without interacting. */}
              <Caption>Our Story</Caption>
              <Accordion alwaysOpen defaultActiveKey={['0']}>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Our Story</Accordion.Header>
                  <Accordion.Body>
                    A sample section rendered on the container background.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                  <Accordion.Header>House Rules</Accordion.Header>
                  <Accordion.Body>
                    A collapsed section — its header uses the page background.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {/* Wifi / password / parking */}
              <Caption>Wifi &amp; parking</Caption>
              <Row className="direction-padding">
                <Col className="network-align">
                  <Wifi size={28} />
                  <div className="ml-8px">
                    <div className="network-title">Wifi Network</div>
                    <div>GuestWiFi</div>
                  </div>
                </Col>
                <Col className="password-align">
                  <div>
                    <div className="password-title">Password</div>
                    <div>welcome123</div>
                  </div>
                </Col>
              </Row>

              {/* Feedback -> rating + social cards */}
              <Caption>Feedback</Caption>
              <div className="text-center mb-8px">
                <ReactStars
                  value={4}
                  size={32}
                  isEdit={false}
                  activeColor="var(--rating-star-active-color)"
                  inactiveColor="var(--rating-star-inactive-color)"
                />
              </div>
              <SocialNetworkCard
                title="Follow us on Instagram"
                icon={<Instagram size={20} />}
              />
              <SocialNetworkCard title="Visit our website" icon={<Globe size={20} />} />

              {/* Agreement modal -> modal background, signature, buttons */}
              <Caption>Agreement modal</Caption>
              <div className="guest-modal-header p-8px">
                <span className="editor-title">Rental Agreement</span>
              </div>
              <div className="guest-modal-body p-8px">
                <p className="form-text">Please review and sign the agreement below.</p>
                {/* Real signature canvas — draw on it to test the ink color. penColor is
                    the resolved ink (canvas strokeStyle can't read CSS vars). */}
                <div
                  className="signature-canvas-container"
                  style={{ position: 'relative' }}
                >
                  {!hasSigned && (
                    <div className="signature-placeholder">Signature here</div>
                  )}
                  <SignatureCanvas
                    canvasProps={{ className: 'signature-canvas' }}
                    penColor={inkColor}
                    onEnd={() => setHasSigned(true)}
                  />
                </div>
                <div className="d-flex justify-content-center mt-8px">
                  <Button className="cancel-button mr-8px">Cancel</Button>
                  <Button className="save-button">Confirm</Button>
                </div>
              </div>

              {/* Payment modal -> the chrome inherits the theme; the Stripe card field is an
                  iframe (mocked here) themed via Stripe's appearance API, not our CSS. */}
              <Caption>Payment modal</Caption>
              <div className="guest-modal-header p-8px">
                <span className="editor-title">Safety Deposit</span>
              </div>
              <div className="guest-modal-body p-8px">
                <p className="form-text">A $100 hold will be placed on your card.</p>
                <div
                  style={{
                    backgroundColor: 'var(--text-input-bg-color)',
                    border: '1px solid var(--guest-border-color)',
                    color: 'var(--secondary-font-color)',
                    padding: '12px 13px',
                    fontSize: '14px',
                  }}
                >
                  1234 1234 1234 1234&nbsp;&nbsp;MM / YY&nbsp;&nbsp;CVC
                </div>
                <span
                  style={{
                    display: 'block',
                    marginTop: '4px',
                    fontSize: '11px',
                    color: '#9ca3af',
                  }}
                >
                  Mock of the Stripe card field — the real one is an iframe themed via
                  Stripe&apos;s appearance API, not CSS.
                </span>
                <div className="d-flex justify-content-center mt-8px">
                  <Button className="cancel-button mr-8px">Cancel</Button>
                  <Button className="save-button">Pay</Button>
                </div>
              </div>

              {/* Alerts appear inside modals -> shown on the modal background so it's clear
                  how the cues read against it (e.g. a red modal would swallow the red
                  danger button / warning). */}
              <Caption>Alerts (on modal background)</Caption>
              <div className="guest-modal-header p-8px">
                <span
                  className="editor-title d-flex align-items-center"
                  style={{ gap: '8px' }}
                >
                  <ExclamationCircle size={22} color="var(--warning-color)" />
                  Please wait
                </span>
              </div>
              <div className="guest-modal-body p-8px">
                <p className="form-text">Please wait while we confirm the charge.</p>
                <span className="error-text">This field is required.</span>
                <div className="d-flex justify-content-center mt-8px">
                  <Button className="cancel-button mr-8px">Cancel</Button>
                  <Button className="delete-button no-border-radius">Remove</Button>
                </div>
              </div>
            </div>
          </Card>
        </Container>
      </GuestPortalContext.Provider>
    </div>
  );
};
